/**
 * Contact Form Modal
 *
 * Modal for adding or editing customer contacts
 * Supports multiple contacts per customer with role assignments
 */

import { useState, useEffect } from 'react';
import { X, User, Save, Briefcase, MapPin } from 'lucide-react';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { Contact, CustomerAddress } from '../types/schema';
import { useContactMutations } from '../hooks/useContacts';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  contact?: Contact | null; // If provided, we're editing; otherwise creating new
  addresses?: CustomerAddress[]; // Optional — enables per-site linking when >1 address
}

type RoleFlag = 'account' | 'site';

export function ContactFormModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  contact,
  addresses = [],
}: ContactFormModalProps) {
  const { createContact, updateContact, loading } = useContactMutations();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [contactTypes, setContactTypes] = useState<RoleFlag[]>(['account', 'site']);
  const [isPrimaryAccount, setIsPrimaryAccount] = useState(false);
  const [isPrimarySite, setIsPrimarySite] = useState(false);
  const [addressIds, setAddressIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (contact) {
      setName(contact.name);
      setPhone(contact.phone || '');
      setEmail(contact.email || '');
      setLabel(contact.role || '');

      // Infer role flags from explicit fields, falling back to legacy isPrimary = both roles.
      const hasExplicit =
        (contact.contactType && contact.contactType.length > 0) ||
        contact.isPrimaryAccount !== undefined ||
        contact.isPrimarySite !== undefined;
      if (hasExplicit) {
        setContactTypes((contact.contactType || []) as RoleFlag[]);
      } else {
        setContactTypes(['account', 'site']);
      }

      setIsPrimaryAccount(!!contact.isPrimaryAccount || (!hasExplicit && !!contact.isPrimary));
      setIsPrimarySite(!!contact.isPrimarySite || (!hasExplicit && !!contact.isPrimary));
      setAddressIds(contact.addressIds || []);
      setNotes(contact.notes || '');
    } else {
      resetForm();
    }
  }, [contact, isOpen]);

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setLabel('');
    setContactTypes(['account', 'site']);
    setIsPrimaryAccount(false);
    setIsPrimarySite(false);
    setAddressIds([]);
    setNotes('');
  };

  const toggleRole = (role: RoleFlag) => {
    setContactTypes((prev) => {
      const next = prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role];
      return next;
    });
    // Clear dependent primary flag if role is removed
    if (role === 'account' && contactTypes.includes('account') && isPrimaryAccount) {
      setIsPrimaryAccount(false);
    }
    if (role === 'site' && contactTypes.includes('site') && isPrimarySite) {
      setIsPrimarySite(false);
      setAddressIds([]);
    }
  };

  const toggleAddress = (id: string) => {
    setAddressIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('Contact name is required');
      return;
    }

    if (!phone.trim() && !email.trim()) {
      alert('At least one contact method (phone or email) is required');
      return;
    }

    if (contactTypes.length === 0) {
      alert('Pick at least one role — Account or Site');
      return;
    }

    try {
      const contactData = {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        role: label.trim() || undefined,
        contactType: contactTypes,
        isPrimaryAccount: contactTypes.includes('account') ? isPrimaryAccount : false,
        isPrimarySite: contactTypes.includes('site') ? isPrimarySite : false,
        addressIds: contactTypes.includes('site') && addressIds.length > 0 ? addressIds : undefined,
        // Legacy flag — keep truthy if either primary is set so older code paths still resolve.
        isPrimary: isPrimaryAccount || isPrimarySite,
        notes: notes.trim() || undefined,
      };

      if (contact?.id) {
        await updateContact(customerId, contact.id, contactData);
      } else {
        await createContact(customerId, contactData);
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact. Please try again.');
    }
  };

  const modalRef = useModalAccessibility({ isOpen, onClose });

  if (!isOpen) return null;

  const isEditing = !!contact?.id;

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isEditing ? 'Edit Contact' : 'Add Contact'}
    >
      <div
        ref={modalRef}
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {isEditing ? 'Edit Contact' : 'Add Contact'}
              </h2>
              <p className="text-sm text-slate-400">{customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder="John Smith"
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
              placeholder="0412 345 678"
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
            />
          </div>

          {/* Contact Type — Account vs Site */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Contact Role <span className="text-slate-400 font-normal">(pick one or both)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => toggleRole('account')}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  contactTypes.includes('account')
                    ? 'bg-blue-500/20 border-blue-400/50 text-blue-100'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                <Briefcase className="w-4 h-4" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Account</div>
                  <div className="text-[10px] opacity-80">Billing &amp; decisions</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => toggleRole('site')}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                  contactTypes.includes('site')
                    ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-100'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                <MapPin className="w-4 h-4" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Site</div>
                  <div className="text-[10px] opacity-80">On-site / day-to-day</div>
                </div>
              </button>
            </div>
          </div>

          {/* Role Label (freeform) */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Label <span className="text-slate-400 font-normal">(optional, e.g. "Farm Manager")</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
              placeholder="General Manager, Spouse, IT Contact..."
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
            />
          </div>

          {/* Primary flags — shown only for roles this contact has */}
          <div className="space-y-2">
            {contactTypes.includes('account') && (
              <label className="flex items-center gap-3 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg cursor-pointer hover:bg-blue-500/15">
                <input
                  type="checkbox"
                  checked={isPrimaryAccount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsPrimaryAccount(e.target.checked)}
                  className="w-5 h-5 rounded bg-white/10 border-white/20 text-blue-400 focus:ring-blue-400 focus:ring-offset-0"
                />
                <Briefcase className="w-4 h-4 text-blue-300" />
                <span className="text-sm font-semibold text-white">Primary account contact</span>
              </label>
            )}
            {contactTypes.includes('site') && (
              <label className="flex items-center gap-3 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg cursor-pointer hover:bg-emerald-500/15">
                <input
                  type="checkbox"
                  checked={isPrimarySite}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsPrimarySite(e.target.checked)}
                  className="w-5 h-5 rounded bg-white/10 border-white/20 text-emerald-400 focus:ring-emerald-400 focus:ring-offset-0"
                />
                <MapPin className="w-4 h-4 text-emerald-300" />
                <span className="text-sm font-semibold text-white">Primary site contact</span>
              </label>
            )}
          </div>

          {/* Per-address linking (only when site role + customer has multiple addresses) */}
          {contactTypes.includes('site') && addresses.length > 1 && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                Site assignment
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Leave all unchecked if this contact applies to every site. Select specific sites to scope.
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {addresses.map((addr) => (
                  <label
                    key={addr.id}
                    className="flex items-center gap-3 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={addressIds.includes(addr.id)}
                      onChange={() => toggleAddress(addr.id)}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 text-emerald-400 focus:ring-emerald-400 focus:ring-offset-0"
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        {addr.label}
                        {addr.isPrimary && (
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">Primary</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{addr.address}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              placeholder="Additional information about this contact..."
              rows={3}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-secondary to-yellow-600 hover:from-brand-secondary/90 hover:to-yellow-500 text-white font-semibold rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Saving...' : isEditing ? 'Update Contact' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
